import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Loader2,
  FileQuestion,
  ListTree,
  ClipboardList,
  BarChart3,
  TrendingUp,
  LineChart,
  KeyRound,
  ExternalLink,
} from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useStudentAttempts } from '@/features/analytics/hooks/useStudentAttempts'
import { useActivationAccess, UnlockDialog, BUY_TEST_SERIES_URL } from '@/features/payments'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'
import { trackDashboardViewed, trackUpgradeCtaClicked } from '@/shared/utils/analytics'
import FeatureCard, { type DashboardFeature } from './FeatureCard'

const PYQ_BY_EXAM: DashboardFeature = {
  id: 'pyq_by_exam',
  title: 'PYQ by Exam',
  description: 'Every past NEET paper, with answers and solutions.',
  path: '/question-bank/by-exam',
  icon: FileQuestion,
  accent: 'blue',
}

const PYQ_BY_SYLLABUS: DashboardFeature = {
  id: 'pyq_by_syllabus',
  title: 'PYQ by Syllabus',
  description: 'Every past question for any unit or topic.',
  path: '/question-bank/by-syllabus',
  icon: ListTree,
  accent: 'amber',
}

const MOCK_TESTS: DashboardFeature = {
  id: 'mock_tests',
  title: 'Full-Length Mock Tests',
  description: 'NEET-pattern mock tests from your test series.',
  path: '/test-series',
  icon: ClipboardList,
  accent: 'green',
  action: 'Go to Test Series',
}

const MY_PERFORMANCE: DashboardFeature = {
  id: 'my_performance',
  title: 'My Performance',
  description: 'Track your scores and accuracy across tests.',
  path: '/analytics/my-performance',
  icon: LineChart,
  accent: 'purple',
}

// Paid users get the whole analytics page; free users only part of it
const PAST_EXAM_ANALYTICS: DashboardFeature = {
  id: 'past_exam_analytics',
  title: 'Past Exam Analytics',
  description: 'Must-study topics, high-yield units and trends from past NEET exams.',
  path: '/analytics/past-exams',
  icon: BarChart3,
  accent: 'indigo',
}

const PAST_EXAMS_OVERVIEW: DashboardFeature = {
  ...PAST_EXAM_ANALYTICS,
  id: 'past_exams',
  title: 'Past Exams Overview',
  description: 'Must-study topics, key numbers and paper history from past NEET exams.',
}

const DETAILED_ANALYTICS: DashboardFeature = {
  id: 'detailed_analytics',
  title: 'Detailed Analytics',
  description: 'High-yield units, subject analysis and trending topics.',
  path: '/analytics/past-exams',
  icon: TrendingUp,
  accent: 'indigo',
}

const FREE_FEATURES = [PYQ_BY_EXAM, PAST_EXAMS_OVERVIEW]
const LOCKED_FEATURES = [MOCK_TESTS, PYQ_BY_SYLLABUS, DETAILED_ANALYTICS, MY_PERFORMANCE]

const Dashboard = () => {
  const dashboardTrackedRef = useRef(false)
  const [lockedFeature, setLockedFeature] = useState<DashboardFeature | null>(null)

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('dashboard', undefined, 'Dashboard')
  }, [])
  const { user } = useAuthStore()
  const studentEmail = user?.email || user?.phoneNumber || ''
  const { isPaidUser, isLoading: isAccessLoading } = useActivationAccess()

  // Fetch completed test attempts
  const { data, isLoading } = useStudentAttempts({
    studentId: studentEmail,
    status: 'SUBMITTED',
    page: 0,
    size: 100, // Fetch enough to calculate stats
  })

  // Calculate stats from attempts
  const totalTestsTaken = data?.page.totalElements ?? 0

  // Track dashboard_viewed when data is loaded
  useEffect(() => {
    if (!isLoading && !dashboardTrackedRef.current) {
      dashboardTrackedRef.current = true
      trackDashboardViewed({
        has_taken_test: totalTestsTaken > 0,
        tests_taken_count: totalTestsTaken,
      })
    }
  }, [isLoading, totalTestsTaken])
  const averageScore = data?.content && data.content.length > 0
    ? data.content.reduce((sum, attempt) => sum + attempt.accuracy, 0) / data.content.length
    : 0

  const handleLockedClick = (feature: DashboardFeature) => {
    trackUpgradeCtaClicked(`dashboard_${feature.id}`)
    setLockedFeature(feature)
  }

  if (isAccessLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    )
  }

  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">Dashboard</h1>

      {isPaidUser ? (
        <>
          {/* Stats Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Tests Taken</p>
                {isLoading ? (
                  <Loader2 className="w-6 h-6 text-blue-500 animate-spin mt-2" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900 mt-2">{totalTestsTaken}</p>
                )}
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Average Accuracy</p>
                {isLoading ? (
                  <Loader2 className="w-6 h-6 text-green-500 animate-spin mt-2" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {totalTestsTaken > 0 ? `${averageScore.toFixed(1)}%` : '-'}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm font-medium">Questions Attempted</p>
                {isLoading ? (
                  <Loader2 className="w-6 h-6 text-purple-500 animate-spin mt-2" />
                ) : (
                  <p className="text-3xl font-bold text-gray-900 mt-2">
                    {data?.content
                      ? data.content.reduce((sum, attempt) => sum + attempt.totalAttempted, 0)
                      : 0}
                  </p>
                )}
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <svg
                  className="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

          <h2 className="mb-3 text-lg font-semibold text-gray-900">Practise</h2>
          <div className="mb-8 space-y-4">
            <FeatureCard
              feature={MOCK_TESTS}
              hero
              stat={isLoading ? undefined : `${totalTestsTaken} ${totalTestsTaken === 1 ? 'test' : 'tests'} taken`}
            />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
              <FeatureCard feature={PYQ_BY_SYLLABUS} />
              <FeatureCard feature={PYQ_BY_EXAM} />
            </div>
          </div>

          <h2 className="mb-3 text-lg font-semibold text-gray-900">Analyse</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            <FeatureCard
              feature={MY_PERFORMANCE}
              stat={!isLoading && totalTestsTaken > 0 ? `Avg accuracy ${averageScore.toFixed(1)}%` : undefined}
            />
            <FeatureCard feature={PAST_EXAM_ANALYTICS} />
          </div>
        </>
      ) : (
        <>
          {/* Unlock banner */}
          <div className="mb-8 flex flex-col gap-4 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 p-5 text-white shadow sm:flex-row sm:items-center sm:justify-between sm:p-6">
            <div>
              <h2 className="text-lg font-semibold sm:text-xl">Unlock everything with a test series</h2>
              <p className="mt-1 text-sm text-blue-100">
                Mock tests, syllabus-wise PYQs and detailed analytics. Got a My NEET Buddy book? Activate its key.
              </p>
            </div>
            <div className="flex flex-shrink-0 flex-col gap-2 sm:flex-row">
              <Link
                to="/test-series"
                onClick={() => trackUpgradeCtaClicked('dashboard_banner_activate')}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-blue-700 transition-colors hover:bg-blue-50"
              >
                <KeyRound className="h-4 w-4" />
                Activate Key
              </Link>
              <a
                href={BUY_TEST_SERIES_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackUpgradeCtaClicked('dashboard_banner_buy')}
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/60 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-white/10"
              >
                Buy a Test Series
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>

          <h2 className="mb-3 text-lg font-semibold text-gray-900">Free for You</h2>
          <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {FREE_FEATURES.map((feature) => (
              <FeatureCard key={feature.id} feature={feature} />
            ))}
          </div>

          <h2 className="mb-3 text-lg font-semibold text-gray-900">Unlock with a Test Series</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4">
            {LOCKED_FEATURES.map((feature) => (
              <FeatureCard key={feature.id} feature={feature} locked onLockedClick={handleLockedClick} />
            ))}
          </div>

          <UnlockDialog
            isOpen={lockedFeature !== null}
            onClose={() => setLockedFeature(null)}
            title={`Unlock ${lockedFeature?.title ?? ''}`}
            description={lockedFeature?.description}
            source={`dashboard_${lockedFeature?.id ?? ''}`}
          />
        </>
      )}
    </div>
  )
}

export default Dashboard
